import { Link, useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import swal from 'sweetalert';
import LoadingBar from 'react-top-loading-bar';
import { useIsOnline } from 'react-use-is-online';
import { useSelector } from 'react-redux';

import { selectCurrentUser } from '../../Features/auth/authSlice';
import { useGetUserEmployeeRolesByUserIdQuery } from '../../Features/usersSlice';

import { PlusIcon } from '@heroicons/react/solid';
import {
    BriefcaseIcon,
    PencilIcon,
    ChevronRightIcon,
} from '@heroicons/react/outline';

function ManageUserEmployeeRolesView() {
    const navigate = useNavigate();
    const user = useSelector(selectCurrentUser);
    const { isOnline } = useIsOnline();

    const [progress, setProgress] = useState(0);
    const [currentItems, setCurrentItems] = useState([]);

    const {
        data: employeeRoles,
        isLoading,
        isSuccess,
        isError,
    } = useGetUserEmployeeRolesByUserIdQuery(user.id);

    useEffect(() => {
        setProgress(30);

        if (isLoading) {
            setProgress(80);
        }

        if (isSuccess) {
            setCurrentItems(employeeRoles?.data || []);
            setProgress(100);
        }

        if (isError) {
            setCurrentItems([]);
            setProgress(100);
        }
    }, [employeeRoles, isLoading, isSuccess, isError]);

    const editRole = (roleId) => {
        navigate(`/client/edit-employee-role/${roleId}`);
    };

    const handleAddRole = (e) => {
        if (!isOnline) {
            e.preventDefault();

            return swal(
                'No Internet connection',
                'Please check Internet connection and try again',
                'error'
            );
        }
    };

    return (
        <div className="w-full min-h-screen bg-gray-100 dark:bg-gray-950 pb-12">
            <LoadingBar
                color="#80b643"
                height="4px"
                progress={progress}
                onLoaderFinished={() => setProgress(0)}
            />

            <div className="w-full px-4 sm:px-6 lg:px-8 pt-6">
                {/* PAGE HEADER */}
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
                    <div className="flex items-center gap-3">
                        <div className="w-11 h-11 rounded-xl bg-[#80b643]/10 dark:bg-[#80b643]/20 flex items-center justify-center">
                            <BriefcaseIcon className="w-6 h-6 text-[#80b643]" />
                        </div>

                        <div>
                            <h1 className="text-lg sm:text-xl font-semibold text-gray-900 dark:text-white">
                                Employee Roles
                            </h1>

                            <p className="text-sm text-gray-500 dark:text-gray-400">
                                Manage roles assigned to your employees
                            </p>
                        </div>
                    </div>

                    <Link
                        to="/client/create-employee-role"
                        onClick={handleAddRole}
                        className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-[#80b643] hover:bg-[#719f3a] text-white text-sm font-medium shadow-sm transition-all duration-200 w-full sm:w-auto"
                    >
                        <PlusIcon className="w-5 h-5" />
                        Add Role
                    </Link>
                </div>

                {/* MAIN CARD */}
                <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl shadow-sm overflow-hidden">
                    {/* CARD HEADER */}
                    <div className="px-4 sm:px-6 py-4 border-b border-gray-200 dark:border-gray-800">
                        <div className="flex items-center gap-3">
                            <div className="w-9 h-9 rounded-lg bg-[#80b643]/10 dark:bg-[#80b643]/20 flex items-center justify-center">
                                <BriefcaseIcon className="w-5 h-5 text-[#80b643]" />
                            </div>

                            <div>
                                <h2 className="text-sm font-semibold text-gray-900 dark:text-white">
                                    Available Roles
                                </h2>

                                <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                                    Select a role to edit its settings
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* LOADING */}
                    {isLoading && (
                        <div className="flex flex-col items-center justify-center py-16">
                            <div className="w-8 h-8 border-2 border-gray-200 dark:border-gray-700 border-t-[#80b643] rounded-full animate-spin"></div>

                            <p className="mt-3 text-sm text-gray-500 dark:text-gray-400">
                                Loading roles...
                            </p>
                        </div>
                    )}

                    {/* ERROR */}
                    {isError && !isLoading && (
                        <div className="flex flex-col items-center justify-center py-16 px-4 text-center">
                            <div className="w-14 h-14 rounded-xl bg-gray-100 dark:bg-gray-800 flex items-center justify-center mb-4">
                                <BriefcaseIcon className="w-7 h-7 text-gray-400" />
                            </div>

                            <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
                                Unable to load roles
                            </h3>

                            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1 max-w-md">
                                We could not retrieve the employee roles.
                                Please try again.
                            </p>

                            <Link
                                to="/client/create-employee-role"
                                onClick={handleAddRole}
                                className="mt-5 inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-[#80b643] hover:bg-[#719f3a] text-white text-sm font-medium shadow-sm transition-all duration-200"
                            >
                                <PlusIcon className="w-5 h-5" />
                                Add Role
                            </Link>
                        </div>
                    )}

                    {/* EMPTY STATE */}
                    {isSuccess &&
                        !isLoading &&
                        currentItems.length === 0 && (
                            <div className="flex flex-col items-center justify-center py-16 px-4 text-center">
                                <div className="w-14 h-14 rounded-xl bg-gray-100 dark:bg-gray-800 flex items-center justify-center mb-4">
                                    <BriefcaseIcon className="w-7 h-7 text-gray-400" />
                                </div>

                                <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
                                    No employee roles
                                </h3>

                                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1 max-w-md">
                                    You haven't created any employee roles yet.
                                </p>

                                <Link
                                    to="/client/create-employee-role"
                                    onClick={handleAddRole}
                                    className="mt-5 inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-[#80b643] hover:bg-[#719f3a] text-white text-sm font-medium shadow-sm transition-all duration-200"
                                >
                                    <PlusIcon className="w-5 h-5" />
                                    Add Role
                                </Link>
                            </div>
                        )}

                    {/* TABLE */}
                    {isSuccess &&
                        !isLoading &&
                        currentItems.length > 0 && (
                            <>
                                {/* DESKTOP TABLE */}
                                <div className="hidden sm:block overflow-x-auto">
                                    <table className="w-full min-w-[500px]">
                                        <thead className="bg-gray-50 dark:bg-gray-800/70 border-b border-gray-200 dark:border-gray-800">
                                            <tr>
                                                <th
                                                    scope="col"
                                                    className="px-6 py-3.5 text-left text-[11px] font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400"
                                                >
                                                    Role
                                                </th>

                                                <th
                                                    scope="col"
                                                    className="px-6 py-3.5 text-right text-[11px] font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400"
                                                >
                                                    Action
                                                </th>
                                            </tr>
                                        </thead>

                                        <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                                            {currentItems.map((data) => (
                                                <tr
                                                    key={data.id}
                                                    onClick={() =>
                                                        editRole(data.id)
                                                    }
                                                    className="group hover:bg-gray-50 dark:hover:bg-gray-800/50 cursor-pointer transition-colors duration-150"
                                                >
                                                    <td className="px-6 py-4">
                                                        <div className="flex items-center gap-3">
                                                            <div className="w-9 h-9 rounded-lg bg-[#80b643]/10 dark:bg-[#80b643]/20 flex items-center justify-center flex-shrink-0">
                                                                <BriefcaseIcon className="w-5 h-5 text-[#80b643]" />
                                                            </div>

                                                            <div>
                                                                <p className="text-sm font-medium text-gray-800 dark:text-gray-200">
                                                                    {data.name}
                                                                </p>

                                                                <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">
                                                                    Employee role
                                                                </p>
                                                            </div>
                                                        </div>
                                                    </td>

                                                    <td className="px-6 py-4">
                                                        <div className="flex justify-end">
                                                            <button
                                                                type="button"
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    editRole(
                                                                        data.id
                                                                    );
                                                                }}
                                                                className="inline-flex items-center justify-center gap-2 px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:border-[#80b643] hover:text-[#80b643] dark:hover:text-[#80b643] text-xs font-medium transition-all duration-200"
                                                            >
                                                                <PencilIcon className="w-4 h-4" />
                                                                Edit
                                                            </button>
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>

                                {/* MOBILE LIST */}
                                <div className="sm:hidden divide-y divide-gray-100 dark:divide-gray-800">
                                    {currentItems.map((data) => (
                                        <button
                                            key={data.id}
                                            type="button"
                                            onClick={() => editRole(data.id)}
                                            className="w-full flex items-center justify-between gap-3 px-4 py-4 text-left hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors duration-150"
                                        >
                                            <div className="flex items-center gap-3 min-w-0">
                                                <div className="w-10 h-10 rounded-lg bg-[#80b643]/10 dark:bg-[#80b643]/20 flex items-center justify-center flex-shrink-0">
                                                    <BriefcaseIcon className="w-5 h-5 text-[#80b643]" />
                                                </div>

                                                <div className="min-w-0">
                                                    <p className="text-sm font-medium text-gray-800 dark:text-gray-200 truncate">
                                                        {data.name}
                                                    </p>

                                                    <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">
                                                        Employee role
                                                    </p>
                                                </div>
                                            </div>

                                            <ChevronRightIcon className="w-5 h-5 text-gray-400 flex-shrink-0" />
                                        </button>
                                    ))}
                                </div>
                            </>
                        )}
                </div>
            </div>
        </div>
    );
}

export default ManageUserEmployeeRolesView;